import ProductTooltip from '@components/shelfDetails/ProductTooltip'
import { cx } from '@utils/cx.ts'

import type { DOMAttributes } from '@react-types/shared'
import type { ProductWrapper } from '@state/product'
import type { TransformedFacing } from '@state/shelf/types'
import type { ReactElement } from 'react'

interface FacingProps {
    product: ProductWrapper | undefined
    facing: TransformedFacing
    className?: string
    children?: ReactElement<DOMAttributes, string>
    state: 'default' | 'dimmed' | 'hovered'
}

export const DATA_FACING_KEY = 'data-facing-id'

/**
 * Adjusts facing dimensions to ensure they fit within shelf boundaries.
 */
function clipShelfDimensions(facing: TransformedFacing) {
  // sometimes the left coordinate is negative, we need to adjust the width and left in that case
  const leftDiff = facing.left < 0 ? -facing.left : 0
  const left = Math.max(facing.left, 0) * 100
  let width = (facing.width - leftDiff) * 100

  // update the width to not overflow the shelf on the right side
  if (left + width > 100) {
    width = 100 - left
  }

  return { left, width }
}

function Facing({ children, product, facing, state, className }: FacingProps) {

  const { left, width } = clipShelfDimensions(facing)

  const facingClasses = cx(
    'absolute h-full max-h-11/12 transition-opacity duration-300',
    state === 'dimmed' && 'opacity-40 duration-0',
    className
  )

  // render product tooltip pointing to the children element
  if (children) {
    return (
      <div
        role='gridcell'
        className={facingClasses}
        style={{ width: `${width}%`, left: `${left}%` }}
        data-testid={`shelf:facing:${facing.id}`}
        {...{ [DATA_FACING_KEY]: facing.id }}
      >
        <ProductTooltip product={product} gtin={facing.gtin}>
          {children}
        </ProductTooltip>
      </div>
    )
  }

  // render product tooltip pointing to the facing div itself
  return (
    <ProductTooltip product={product} gtin={facing.gtin}>
      <div
        role='gridcell'
        className={facingClasses}
        style={{ width: `${width}%`, left: `${left}%` }}
        data-testid={`shelf:facing:${facing.id}`}
        {...{ [DATA_FACING_KEY]: facing.id }}
      />
    </ProductTooltip>
  )
}

export default Facing